"use client";

import type { CustomLinkProps } from "@dafiti/core-layout";
import type { MouseEvent } from "react";

import { Link } from "./Link";

const LOGIN_PATHS = new Set(["/login", "/customer/account/login"]);

/**
 * Drop-in `LinkComponent` prop for `MainAppLayout`. Renders via `Link` (next/link by
 * default, `useRouterLink={false}` for a plain anchor). On any click of a
 * login href it rewrites the target to `${href}?returnUrl=<absolute current
 * URL>` at click time, so auth-xp redirects back to the triggering page
 * instead of NEXT_PUBLIC_DEFAULT_RETURN_URL — click-time avoids the SSR
 * hydration race, since `window.location.href` is only reliably defined then.
 */
export function MainAppLayoutLink({
  href,
  children,
  className,
  onClick,
  onMouseDown,
  useRouterLink = true,
  ...props
}: CustomLinkProps) {
  function handleClick(e: MouseEvent<HTMLAnchorElement>) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey)
      return;

    onClick?.(e);

    if (!isLoginHref(href) || e.defaultPrevented) return;

    e.preventDefault();
    try {
      const target = new URL(href, window.location.origin);
      target.searchParams.set("returnUrl", window.location.href);
      window.location.href = target.toString();
    } catch (error) {
      console.error("[MainAppLayoutLink] Failed to parse URL:", href, error);
      window.location.href = href;
    }
  }

  return (
    <Link
      href={href}
      useRouterLink={useRouterLink}
      className={className}
      onClick={handleClick}
      onMouseDown={onMouseDown}
      {...props}
    >
      {children}
    </Link>
  );
}

function isLoginHref(href: string): boolean {
  try {
    return LOGIN_PATHS.has(
      new URL(href, "https://placeholder.invalid").pathname,
    );
  } catch {
    return false;
  }
}
